import { DateTime } from 'luxon';
import { IStopArea } from '../../../../../models/stop-area.model';
import { t } from '../../../../../services/texts.service';
import { CommuterParkingLotApiModel } from '../../../../StopAreas/models/commuter-parking-lot.api-model';
import useFetch, { FetchConfig, Status } from '../../../../../hooks/fetch.hook';
import { ICommuterParkingLot } from '../../../../../models/commuter-parking-lot.model';
import Spinner from '../../../../Shared/Spinner';
import Alert from '../../../../Shared/Alert';
import { DistanceHelper } from '../../../../../helpers/distance.helper';
import { useMemo } from 'react';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { IconVariant } from '../../../../Shared/Icon/icon-variant.enum';
import { Icon } from '../../../../Shared/Icon';

function processReceivedData(data: CommuterParkingLotApiModel[] | undefined): ICommuterParkingLot[] {
  return data?.map(CommuterParkingLotApiModel.mapToBusinessModel) ?? [];
}

export default function CommuterParkingLots({ stopAreas, autoRefreshTimestamp, className }: { stopAreas: IStopArea[]; autoRefreshTimestamp: DateTime; className?: string }) {
  // === CONSTANTS ===
  const MAX_DISTANCE_METERS = 500;

  // === STATE ===
  const [{ status, data }] = useFetch<CommuterParkingLotApiModel[]>('/api/stop-area-search/commuterparkinglots', false, false, FetchConfig.default, [autoRefreshTimestamp]);

  // === VARIABLES ===
  const stopAreasWithCoordinates = useMemo(() => stopAreas.filter((stopArea) => stopArea.latitude && stopArea.longitude), [stopAreas]);
  //  CommuterParkingLots list contains nearby parking lots within the max distance, with duplicates by name removed and the remaining items ordered by shortest distance to the selected stop areas.
  const commuterParkingLots = useMemo(() => {
    // Keep only the closest occurrence per parking lot name before sorting the final list.
    const closestParkingLots = new Map<string, { commuterParkingLot: ICommuterParkingLot; distance: number }>();

    processReceivedData(data)
      .filter((commuterParkingLot) => commuterParkingLot.latitude && commuterParkingLot.longitude)
      .forEach((commuterParkingLot) => {
        // When two stop areas are selected, use the shortest distance to either one.
        const distance = Math.min(
          ...stopAreasWithCoordinates.map((stopArea) => DistanceHelper.calculateDistanceInMeters(stopArea.latitude!, stopArea.longitude!, commuterParkingLot.latitude!, commuterParkingLot.longitude!)),
        );

        if (distance > MAX_DISTANCE_METERS) return;

        const existingParkingLot = closestParkingLots.get(commuterParkingLot.name);
        if (!existingParkingLot || distance < existingParkingLot.distance) {
          closestParkingLots.set(commuterParkingLot.name, { commuterParkingLot, distance });
        }
      });

    return Array.from(closestParkingLots.values())
      .sort((a, b) => a.distance - b.distance)
      .map(({ commuterParkingLot }) => commuterParkingLot);
  }, [data, stopAreasWithCoordinates]);

  const hasSingleStopArea = stopAreas.length === 1;
  const noParkingLotsText = hasSingleStopArea
    ? t(`Hittade inga pendelparkeringar inom ${MAX_DISTANCE_METERS} meter från din hållplats.`, `Found no commuter parking lots within ${MAX_DISTANCE_METERS} meters from your stop area.`)
    : t(`Hittade inga pendelparkeringar inom ${MAX_DISTANCE_METERS} meter från dina hållplatser.`, `Found no commuter parking lots within ${MAX_DISTANCE_METERS} meters from your stop areas.`);
  const parkingLotsText = hasSingleStopArea
    ? t(`Hittade parkeringsplatser inom ${MAX_DISTANCE_METERS} meter från din hållplats.`, `Found commuter parking lots within ${MAX_DISTANCE_METERS} meters from your stop area.`)
    : t(`Hittade parkeringsplatser inom ${MAX_DISTANCE_METERS} meter från dina hållplatser.`, `Found commuter parking lots within ${MAX_DISTANCE_METERS} meters from your stop areas.`);

  return (
    <div className={className ?? ''}>
      <h3>{t('Pendelparkeringar', 'Commuter parking lots')}</h3>
      {status === Status.loading && <Spinner className="my-3" />}
      <Alert
        heading={t('Ett fel uppstod vid hämtning av pendelparkeringar. Försök igen senare.', 'An error occurred while fetching commuter parking lots. Please try again later.')}
        closable={false}
        show={status === Status.failed}
      />
      {status === Status.success && (
        <>
          {commuterParkingLots.length === 0 && <div>{noParkingLotsText}</div>}
          {commuterParkingLots.length > 0 && (
            <div>
              <p>{parkingLotsText}</p>
              <ul className="list-unstyled d-flex flex-column gap-2">
                {commuterParkingLots.map((cpl) => (
                  <li key={cpl.name}>
                    <CommuterParkingLot commuterParkingLot={cpl} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CommuterParkingLot({ commuterParkingLot: cpl }: { commuterParkingLot: ICommuterParkingLot }) {
  function calculateStatusClassName(occupancyRate: number): string {
    if (occupancyRate < 0.2) {
      return 'bg-danger';
    }
    if (occupancyRate < 0.4) {
      return 'bg-warning';
    }
    return 'bg-success';
  }

  const occupancyRate = Math.round(((cpl.freeSpaces ?? 0) / cpl.totalCapacity) * 100) / 100;

  return (
    <div className="d-flex flex-column gap-2 p-2 p-sm-3 bg-white border rounded">
      <div className="d-flex align-items-center">
        <Icon motif={IconMotif.Car} variant={IconVariant.SquareFilled} altText={t('Pendelparkering', 'Commuter parking lot')} className="me-1" />
        <h4 className="text-break mb-0">{cpl.name}</h4>
      </div>
      {cpl.freeSpaces === undefined && (
        <div>
          <div className="mb-2">
            <span className="text-muted">{`${cpl.totalCapacity} ${t('platser totalt', 'spaces total')}`}</span>
          </div>
          <div className="text-end">
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${cpl.latitude},${cpl.longitude}`} target="_blank" rel="noopener noreferrer">
              {t('Vägbeskrivning', 'Get directions')}
            </a>
          </div>
        </div>
      )}
      {cpl.freeSpaces !== undefined && (
        <div>
          <div className="progress mb-2">
            <div
              className={`progress-bar ${calculateStatusClassName(occupancyRate)}`}
              role="progressbar"
              style={{ width: `${occupancyRate * 100}%` }}
              aria-valuenow={occupancyRate * 100}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>
          <div className="mb-2">
            <span className="fw-bold">{`${cpl.freeSpaces} ${cpl.freeSpaces === 1 ? t('plats ledig', 'space available') : t('platser lediga', 'spaces available')}`}</span>{' '}
            <span className="text-muted">{t(`av totalt ${cpl.totalCapacity} platser`, `of a total of ${cpl.totalCapacity} spaces`)}</span>
          </div>
          <div className="text-end">
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${cpl.latitude},${cpl.longitude}`} target="_blank" rel="noopener noreferrer">
              {t('Vägbeskrivning', 'Get directions')}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}